import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import { createQuickRequest } from '../../services/propertyInquiryService';
import { createAppointment } from '../../services/appointmentService';

const QuickRequestModal = ({ isOpen, onClose, requestType, propertyInfo = {} }) => {
  const [userInfo, setUserInfo] = useState({
    phone: '',
    email: '',
    preferred_contact_time: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear field error on change
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Phone validation
    const phone = userInfo?.phone?.trim();
    if (!phone) {
      newErrors.phone = 'Le numéro de téléphone est obligatoire.';
    } else if (!/^[+]?[\d\s\-().]{7,20}$/?.test(phone)) {
      newErrors.phone = 'Veuillez entrer un numéro de téléphone valide.';
    }

    // Email validation
    const email = userInfo?.email?.trim();
    if (!email) {
      newErrors.email = "L'adresse email est obligatoire.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      if (requestType === 'visite') {
        // Create appointment request
        const appointmentData = {
          appointment_type: 'visite',
          scheduled_date: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString(),
          notes: `Demande de visite rapide${propertyInfo?.title ? ` pour ${propertyInfo?.title}` : ''}. Téléphone: ${userInfo?.phone || 'Non fourni'}. Email: ${userInfo?.email || 'Non fourni'}. Heure préférée: ${userInfo?.preferred_contact_time || 'Non spécifiée'}`,
          status: 'scheduled',
          client_id: null,
          agent_id: null,
          property_id: propertyInfo?.propertyId || null,
          duration_minutes: 60
        };

        const { error: appointmentError } = await createAppointment(appointmentData);
        if (appointmentError) {
          setError(appointmentError);
          return;
        }

        // Also save to property_inquiries with email
        await createQuickRequest(requestType, userInfo, propertyInfo);
      } else {
        const { error: inquiryError } = await createQuickRequest(requestType, userInfo, propertyInfo);
        if (inquiryError) {
          setError(inquiryError);
          return;
        }
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setUserInfo({ phone: '', email: '', preferred_contact_time: '' });
        setErrors({});
      }, 2500);

    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Icon name="X" size={24} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-full ${requestType === 'visite' ? 'bg-purple-100' : 'bg-green-100'}`}>
              <Icon
                name={requestType === 'visite' ? 'Play' : 'Info'}
                size={20}
                color={requestType === 'visite' ? '#7C3AED' : '#059669'}
              />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              {requestType === 'visite' ? 'Demande de Visite' : "Demande d'Informations"}
            </h3>
          </div>
          <p className="text-gray-600">
            {requestType === 'visite' ?'Planifiez une visite de nos propriétés disponibles' : "Obtenez plus d'informations sur nos services"}
          </p>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Icon name="Check" size={32} color="#059669" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Demande envoyée!</h4>
            <p className="text-gray-600">
              Nous vous contactons bientôt pour{' '}
              {requestType === 'visite' ? 'planifier votre visite' : 'vous fournir les informations'}.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Phone input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro de téléphone <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                placeholder="+225 XX XX XX XX XX"
                value={userInfo?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
              />
              {errors?.phone && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} color="#DC2626" />
                  {errors?.phone}
                </p>
              )}
            </div>

            {/* Email input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder="exemple@email.com"
                value={userInfo?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
              />
              {errors?.email && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} color="#DC2626" />
                  {errors?.email}
                </p>
              )}
            </div>

            {/* Preferred contact time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heure préférée pour être contacté
              </label>
              <select
                value={userInfo?.preferred_contact_time}
                onChange={(e) => handleInputChange('preferred_contact_time', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent"
              >
                <option value="">Sélectionner une heure</option>
                <option value="morning">Matin (8h-12h)</option>
                <option value="afternoon">Après-midi (12h-17h)</option>
                <option value="evening">Soir (17h-20h)</option>
              </select>
            </div>

            {/* Global error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} color="#DC2626" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Envoi en cours...</span>
                </div>
              ) : (
                `Envoyer la demande ${requestType === 'visite' ? 'de visite' : "d'informations"}`
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default QuickRequestModal;